import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Flex,
  Text,
  Popup,
  Input,
  HighlightIcon,
  InfoIcon,
  AcceptIcon,
  CloseIcon,
  EditIcon,
  QuestionCircleIcon,
  Tooltip,
} from "@fluentui/react-northstar";
import { Row, Col } from "antd";
import "./AnnotationSystem.scss";
import AnnotationsSummary from "./AnnotationsSummary";
import MenuBookIcon from "@mui/icons-material/MenuBook";

const annotationTypes = [
  {
    type: "highlight",
    icon: HighlightIcon,
    label: "Highlight",
    backgroundColor: "#ffde9b9c",
    color: "#faad14",
  },
  {
    type: "suggestion",
    icon: InfoIcon,
    label: "Suggestion",
    backgroundColor: "#e6f7ff",
    color: "#1890ff",
  },
  {
    type: "correction",
    icon: CloseIcon,
    label: "Correction",
    backgroundColor: "#fff1f0",
    color: "#ff4d4f",
  },
  {
    type: "praise",
    icon: AcceptIcon,
    label: "Praise",
    backgroundColor: "#f6ffed",
    color: "#52c41a",
  },
  {
    type: "question",
    icon: QuestionCircleIcon,
    label: "Question",
    backgroundColor: "#eae6ff",
    color: "#6264a7",
  },
  {
    type: "reference",
    icon: MenuBookIcon,
    label: "Reference",
    backgroundColor: "#c2410c10",
    color: "#c2410c",
  },
];

const AnnotationToolbar = ({ onAnnotate, isAnnotating, onCancel }) => {
  return (
    <Row gutter={[5, 8]} className="annotation-toolbar">
      {!isAnnotating ? (
        annotationTypes.map(
          ({ type, icon: Icon, label, backgroundColor, color }) => (
            <Col key={type}>
              <Button
                icon={<Icon outline />}
                data-cy={`annotate-btn-${type}`}
                content={label}
                onClick={() => onAnnotate(type)}
                style={{
                  backgroundColor: backgroundColor,
                  color: color,
                  padding: "0px 10px",
                  boxShadow: "none",
                }}
              />
            </Col>
          )
        )
      ) : (
        <Flex gap="gap.small" vAlign="center">
          <Text content="Select text to annotate" />
          <Button size="small" content="Cancel" onClick={onCancel} />
        </Flex>
      )}
    </Row>
  );
};

const AnnotatedText = ({ text = "", annotations = [], onAnnotationClick }) => {
  const sortedAnnotations =
    annotations && annotations.length
      ? [...annotations].sort((a, b) => a.startIndex - b.startIndex)
      : [];
  const segments = [];
  let lastIndex = 0;

  sortedAnnotations.forEach((annotation, index) => {
    if (annotation.startIndex > lastIndex) {
      segments.push(
        <span key={`text-${index}`}>
          {text.substring(lastIndex, annotation.startIndex)}
        </span>
      );
    }

    const annotationType = annotationTypes.find(
      (t) => t.type === annotation.type
    );
    segments.push(
      <Tooltip
        key={`annotation-${annotation.id}`}
        content={annotation.comment}
        trigger={
          <span
            className="annotated-text"
            style={{
              backgroundColor: annotationType.backgroundColor,
              borderBottom: `2px solid ${annotationType.color}`,
            }}
            onClick={(e) => onAnnotationClick(annotation.id, e)}
          >
            {text.substring(annotation.startIndex, annotation.endIndex)}
          </span>
        }
        mountNode={
          document.getElementsByClassName("student-view-dialog")?.[0] ||
          document.getElementsByClassName("quick-view-dialog")?.[0] ||
          document.getElementsByClassName("grading-summary-dialog")?.[0]
        }
        zIndex={10}
      />
    );

    lastIndex = annotation.endIndex;
  });

  if (text && lastIndex < text.length) {
    segments.push(<span key="text-end">{text.substring(lastIndex)}</span>);
  }

  return <>{segments}</>;
};

const AnnotationSystem = ({
  essayText,
  isAnnotationMode = false,
  viewMode = "teacher",
  onAnnotationAdd,
  onAnnotationDelete,
  onAnnotationEdit,
  annotations = [],
}) => {
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [annotationType, setAnnotationType] = useState("highlight");
  const [selectedText, setSelectedText] = useState("");
  const [selectedRange, setSelectedRange] = useState(null);
  const [comment, setComment] = useState("");
  const [activeAnnotationId, setActiveAnnotationId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editComment, setEditComment] = useState("");
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const textRef = useRef(null);

  const handleAnnotate = (type) => {
    setAnnotationType(type);
    setIsAnnotating(true);
  };

  const handleCancel = () => {
    setIsAnnotating(false);
    setSelectedText("");
    setSelectedRange(null);
    setComment("");
  };

  const handleTextSelection = () => {
    if (!isAnnotating) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const textElement = textRef.current;

    if (textElement && textElement.contains(range.commonAncestorContainer)) {
      const text = selection.toString().trim();
      if (text) {
        const fullText = textElement.textContent || "";
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(textElement);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;

        setSelectedText(text);
        setSelectedRange({ start, end: start + text.length });
      }
    }
  };

  const handleAddAnnotation = () => {
    if (selectedText && selectedRange && comment) {
      onAnnotationAdd({
        id: Math.random().toString(36).substring(2, 15),
        startIndex: selectedRange.start,
        endIndex: selectedRange.end,
        text: selectedText,
        comment,
        type: annotationType,
      });

      handleCancel();
    }
  };

  const handleAnnotationClick = (id, event) => {
    setActiveAnnotationId(id);
    // Get the clicked element's position
    const rect = event.target.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseAnnotation = () => {
    setActiveAnnotationId(null);
    setIsEditing(false);
    setEditComment("");
  };

  const handleEditAnnotation = (annotation) => {
    setIsEditing(true);
    setEditComment(annotation.comment);
    setActiveAnnotationId(annotation.id);
  };

  const handleUpdateAnnotation = () => {
    if (activeAnnotationId && editComment) {
      onAnnotationEdit(activeAnnotationId, editComment);
      handleCloseAnnotation();
    }
  };

  const handleDeleteAnnotation = (id) => {
    onAnnotationDelete(id);
    handleCloseAnnotation();
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleTextSelection);
    return () => {
      document.removeEventListener("mouseup", handleTextSelection);
    };
  }, [isAnnotating]);

  const activeAnnotation =
    annotations &&
    annotations.length &&
    annotations.find((a) => a.id === activeAnnotationId);
  const annotationTypeInfo = annotationTypes.find(
    (t) => t.type === (activeAnnotation?.type || annotationType)
  );

  return (
    <div className="annotation-system">
      {viewMode === "teacher" && isAnnotationMode && (
        <AnnotationToolbar
          onAnnotate={handleAnnotate}
          isAnnotating={isAnnotating}
          onCancel={handleCancel}
        />
      )}

      <div ref={textRef} className="text-content">
        <div className="text-body" data-cy="annotate-text">
          <AnnotatedText
            text={essayText}
            annotations={annotations}
            onAnnotationClick={handleAnnotationClick}
          />
        </div>
      </div>

      {selectedText && selectedRange && (
        <div className="annotation-input">
          <Flex gap="gap.small" vAlign="center">
            {annotationTypeInfo && (
              <annotationTypeInfo.icon
                outline
                style={{ color: annotationTypeInfo.color }}
              />
            )}
            <Text content={`Add ${annotationType} for:`} weight="semibold" />
          </Flex>
          <div className="selected-text">"{selectedText}"</div>
          <Input
            data-cy="annotation-comment-input"
            fluid
            placeholder={`Add your ${annotationType} comment...`}
            value={comment}
            onChange={(e, data) => setComment(data.value)}
          />
          <Flex gap="gap.small" style={{ marginTop: "8px" }}>
            <Button
              content="Cancel"
              style={{ padding: "0px 10px", borderRadius: "5px" }}
              onClick={handleCancel}
            />
            <Button
              data-cy="annotation-add-btn"
              primary
              content={`Add ${annotationType}`}
              onClick={handleAddAnnotation}
              disabled={!comment.trim()}
              style={{ padding: "0px 10px", borderRadius: "5px" }}
            />
          </Flex>
        </div>
      )}

      {activeAnnotation ? (
        <Popup
          open={!!activeAnnotationId}
          onOpenChange={handleCloseAnnotation}
          trigger={
            <div
              style={{
                position: "absolute",
                // top: popupPosition.top,
                // left: popupPosition.left,
              }}
            />
          }
          content={
            <div
              className="annotation-popup"
              style={{ minWidth: "400px", maxWidth: "500px" }}
            >
              <Flex column gap="gap.small">
                <Flex gap="gap.smaller" vAlign="center">
                  {annotationTypeInfo && (
                    <annotationTypeInfo.icon
                      outline
                      style={{ color: annotationTypeInfo.color }}
                    />
                  )}

                  <Text
                    content={annotationTypeInfo.label}
                    weight="semibold"
                    size="large"
                  />
                </Flex>
                <div
                  style={{
                    backgroundColor: "#f9fafb",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  "{activeAnnotation.text}"
                </div>
                {isEditing ? (
                  <>
                    <Input
                      fluid
                      value={editComment}
                      onChange={(e, data) => setEditComment(data.value)}
                    />
                    <Flex
                      gap="gap.small"
                      hAlign="end"
                      style={{ padding: "10px 0px" }}
                    >
                      <Button
                        content="Cancel"
                        onClick={handleCloseAnnotation}
                        style={{
                          padding: "0px 10px",
                          borderRadius: "5px",
                        }}
                      />
                      <Button
                        primary
                        content="Update"
                        onClick={handleUpdateAnnotation}
                        disabled={!editComment.trim()}
                        style={{
                          padding: "0px 10px",
                          borderRadius: "5px",
                        }}
                      />
                    </Flex>
                  </>
                ) : (
                  <>
                    <div className="annotation-comment">
                      {activeAnnotation.comment}
                    </div>
                    {viewMode === "teacher" && isAnnotationMode && (
                      <Flex
                        gap="gap.smaller"
                        hAlign="end"
                        style={{ padding: "10px 0px" }}
                      >
                        <Button
                          icon={<EditIcon />}
                          content="Edit"
                          style={{
                            padding: "0px 10px",
                            borderRadius: "5px",
                          }}
                          onClick={() => handleEditAnnotation(activeAnnotation)}
                        />
                        <Button
                          icon={<CloseIcon />}
                          content="Delete"
                          onClick={() =>
                            handleDeleteAnnotation(activeAnnotation.id)
                          }
                          style={{
                            padding: "0px 10px",
                            borderRadius: "5px",
                            color: "#ff4d4f",
                          }}
                        />
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </div>
          }
          mountNode={
            document.getElementsByClassName("student-view-dialog")?.[0] ||
            document.getElementsByClassName("quick-view-dialog")?.[0] ||
            document.getElementsByClassName("grading-summary-dialog")?.[0]
          }
          zIndex={10}
        />
      ) : null}

      {annotations.length > 0 ? (
        <AnnotationsSummary
          annotations={annotations}
          handleAnnotationClick={handleAnnotationClick}
        />
      ) : null}
    </div>
  );
};

export default AnnotationSystem;
